import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { useAuth } from "../contexts/AuthContext";
import {
  fetchSystemAccessStatus,
  refreshSystemSession,
} from "../services/systemAccessService";
import decodeJwtToken from "../auth/decodeJwtToken";
import { getPostAuthRoute } from "../shared/systemAccessRouting";

const brand = "#800b33";

const formatCurrency = (value) =>
  `TZS ${new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0))}`;

const FeeCard = ({ title, invoice, highlight = false }) => {
  const isPaid = invoice?.outstanding_amount === "0.00";

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: highlight ? "1px solid rgba(128, 11, 51, 0.25)" : "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: highlight ? 4 : 1,
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Chip
              size="small"
              color={isPaid ? "success" : invoice?.is_overdue ? "error" : "warning"}
              icon={isPaid ? <CheckCircleOutlineOutlinedIcon /> : <LockClockOutlinedIcon />}
              label={isPaid ? "Paid" : invoice?.status || "Pending"}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Amount due: <strong>{formatCurrency(invoice?.amount_due)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Amount paid: <strong>{formatCurrency(invoice?.amount_paid)}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Outstanding: <strong>{formatCurrency(invoice?.outstanding_amount)}</strong>
          </Typography>
          {invoice?.due_date && (
            <Typography variant="body2" color="text.secondary">
              Deadline: <strong>{invoice.due_date}</strong>
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function SystemFeeAccessPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await fetchSystemAccessStatus();
      setStatus(payload);
    } catch (err) {
      setError(err.message || "Unable to load system fee status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const currentActionLabel = useMemo(() => {
    if (status?.next_required_action === "PAY_SRF") {
      return "Complete System Registration Fee (SRF)";
    }
    if (status?.next_required_action === "PAY_SAF") {
      return "Complete System Annual Fee (SAF)";
    }
    return "System Fee Status";
  }, [status]);

  const handleRefreshAccess = async () => {
    setRefreshing(true);
    setError("");
    try {
      const latestStatus = await fetchSystemAccessStatus();
      setStatus(latestStatus);

      if (!latestStatus?.has_full_access) {
        Swal.fire({
          icon: "info",
          title: "Payment Still Pending",
          text: "No completed system payment has been detected yet. Refresh again after the bank confirms payment.",
          confirmButtonColor: brand,
        });
        return;
      }

      const session = await refreshSystemSession();
      login(session.access, session.refresh);
      const decoded = decodeJwtToken(session.access);
      Swal.fire({
        icon: "success",
        title: "Access Restored",
        text: "Your session has been refreshed with the latest access rights.",
        confirmButtonColor: brand,
      }).then(() => {
        navigate(getPostAuthRoute(decoded), { replace: true });
      });
    } catch (err) {
      setError(err.message || "Unable to refresh system access right now.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8f5f8",
        px: { xs: 2, md: 4 },
        py: { xs: 4, md: 6 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
        <Stack spacing={3}>
          <Card sx={{ borderRadius: 4, boxShadow: 5 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={2.5}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(128, 11, 51, 0.1)",
                        color: brand,
                      }}
                    >
                      <ReceiptLongOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: brand }}>
                        {currentActionLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your platform access depends on completion of mandatory system fees.
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<AutorenewOutlinedIcon />}
                      onClick={handleRefreshAccess}
                      disabled={loading || refreshing}
                    >
                      {refreshing ? "Refreshing..." : "Refresh Payment Status"}
                    </Button>
                  </Stack>
                </Stack>

                {loading ? (
                  <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {error && <Alert severity="error">{error}</Alert>}
                    {status?.restriction_message && (
                      <Alert severity="warning">{status.restriction_message}</Alert>
                    )}
                    <Alert severity="info">
                      Use this control number for both SRF and SAF payments:{" "}
                      <strong>{status?.system_payment_reference || user?.system_payment_reference || "Not available"}</strong>.
                      These system fees do not include SSC charges.
                    </Alert>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FeeCard
                          title="System Registration Fee (SRF)"
                          invoice={status?.registration_fee}
                          highlight={status?.next_required_action === "PAY_SRF"}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FeeCard
                          title="System Annual Fee (SAF)"
                          invoice={status?.annual_fee}
                          highlight={status?.next_required_action === "PAY_SAF"}
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Payment notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        SRF is a one-time activation fee. SAF is yearly and can be settled in installments before the deadline.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        After payment, click <strong>Refresh Payment Status</strong> to reload your access using the latest backend state.
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}
